import { AlertCircle, RefreshCw, HelpCircle } from 'lucide-react';
import { clsx } from 'clsx';

interface ErrorStateProps {
  title?: string;
  message: string;
  helpText?: string;
  onRetry?: () => void;
  onContactSupport?: () => void;
  variant?: 'default' | 'inline' | 'card';
  className?: string;
}

export function ErrorState({
  title = 'Something went wrong',
  message,
  helpText,
  onRetry,
  onContactSupport,
  variant = 'default',
  className,
}: ErrorStateProps) {
  if (variant === 'inline') {
    return (
      <div
        className={clsx(
          'flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3',
          className
        )}
        role="alert"
      >
        <AlertCircle className="h-5 w-5 flex-shrink-0 text-red-500" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-red-800">{title}</p>
          <p className="text-sm text-red-700">{message}</p>
        </div>
        {onRetry && (
          <button
            onClick={onRetry}
            className="flex-shrink-0 rounded-lg px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-100 transition-colors"
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  if (variant === 'card') {
    return (
      <div
        className={clsx(
          'rounded-xl border border-red-200 bg-red-50 p-6',
          className
        )}
        role="alert"
      >
        <div className="flex items-start gap-4">
          <div className="rounded-full bg-red-100 p-3">
            <AlertCircle className="h-6 w-6 text-red-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <p className="mt-1 text-sm text-gray-600">{message}</p>
            {helpText && (
              <p className="mt-2 flex items-center gap-1.5 text-xs text-gray-500">
                <HelpCircle className="h-3.5 w-3.5" />
                {helpText}
              </p>
            )}
            <div className="mt-4 flex gap-3">
              {onRetry && (
                <button
                  onClick={onRetry}
                  className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Try Again
                </button>
              )}
              {onContactSupport && (
                <button
                  onClick={onContactSupport}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 transition-colors"
                >
                  Contact Support
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default variant - centered
  return (
    <div
      className={clsx(
        'rounded-xl border border-red-200 bg-red-50 p-8 text-center',
        className
      )}
      role="alert"
    >
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
        <AlertCircle className="h-7 w-7 text-red-600" />
      </div>
      <h3 className="mt-4 text-lg font-semibold text-gray-900">{title}</h3>
      <p className="mt-2 text-sm text-gray-600 max-w-md mx-auto">{message}</p>
      {helpText && (
        <p className="mt-2 flex items-center justify-center gap-1.5 text-xs text-gray-500">
          <HelpCircle className="h-3.5 w-3.5" />
          {helpText}
        </p>
      )}
      <div className="mt-6 flex justify-center gap-3">
        {onRetry && (
          <button
            onClick={onRetry}
            className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          >
            <RefreshCw className="h-4 w-4" />
            Try Again
          </button>
        )}
        {onContactSupport && (
          <button
            onClick={onContactSupport}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 transition-colors"
          >
            Contact Support
          </button>
        )}
      </div>
    </div>
  );
}
